import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import PortalAxios, { baseURL } from '../../services/PortalAxios';
import { toast } from 'react-toastify';

export const checkout = createAsyncThunk(
    'portalOrder/checkout',
    async ({ customerNote, paymentMethod, deliveryAddress }, { rejectWithValue }) => {
        try {
            const res = await PortalAxios.PortalRequest({
                method: 'post',
                url: `${baseURL}/portal/orders/checkout`,
                data: { customerNote, paymentMethod, deliveryAddress }
            });
            toast.success('Order placed successfully!');
            return res.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Checkout failed');
        }
    }
);

export const getMyOrders = createAsyncThunk(
    'portalOrder/getMyOrders',
    async (filters, { rejectWithValue }) => {
        try {
            const query = new URLSearchParams(filters).toString();
            const res = await PortalAxios.PortalRequest({
                method: 'get',
                url: `${baseURL}/portal/orders?${query}`
            });
            return res.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to fetch orders');
        }
    }
);

export const getMyOrderById = createAsyncThunk(
    'portalOrder/getMyOrderById',
    async (id, { rejectWithValue }) => {
        try {
            const res = await PortalAxios.PortalRequest({
                method: 'get',
                url: `${baseURL}/portal/orders/${id}`
            });
            return res.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to fetch order details');
        }
    }
);

export const cancelOrder = createAsyncThunk(
    'portalOrder/cancelOrder',
    async (id, { rejectWithValue }) => {
        try {
            const res = await PortalAxios.PortalRequest({
                method: 'delete',
                url: `${baseURL}/portal/orders/${id}/cancel`
            });
            toast.success('Order cancelled successfully');
            return res.data.data;
        } catch (err) {
            return rejectWithValue(err.response?.data?.message || 'Failed to cancel order');
        }
    }
);

const portalOrderSlice = createSlice({
    name: 'portalOrder',
    initialState: {
        orders: [],
        currentOrder: null,
        loading: false,
        pagination: null,
        error: null,
    },
    reducers: {
        clearCurrentOrder: (state) => { state.currentOrder = null; }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getMyOrders.pending, (state) => { state.loading = true; })
            .addCase(getMyOrders.fulfilled, (state, action) => {
                state.loading = false;
                state.orders = action.payload.list;
                state.pagination = action.payload.pagination;
            })
            .addCase(getMyOrders.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(getMyOrderById.pending, (state) => { state.loading = true; })
            .addCase(getMyOrderById.fulfilled, (state, action) => {
                state.loading = false;
                state.currentOrder = action.payload;
            })
            .addCase(getMyOrderById.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(checkout.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(checkout.fulfilled, (state, action) => {
                state.loading = false;
                state.currentOrder = action.payload.order;
            })
            .addCase(checkout.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            .addCase(cancelOrder.fulfilled, (state, action) => {
                state.currentOrder = action.payload;
                state.orders = state.orders.map(o => o._id === action.payload._id ? action.payload : o);
            });
    }
});

export const { clearCurrentOrder } = portalOrderSlice.actions;
export default portalOrderSlice.reducer;
